import i18n from '../i18n';

const t = i18n.t.bind(i18n);

/**
 * Parse "driver_license:NZ" -> { idType: 'driver_license', countryCode: 'NZ' }
 */
export const parseIdKey = key => {
  if (typeof key !== 'string') return { idType: null, countryCode: null, raw: key };
  const [type, cc] = key?.split?.(':', 2);
  const idType = (type || '').trim();
  const countryCode = cc ? cc.trim().toUpperCase() : null;
  return { idType, countryCode, raw: key };
};

/** Build canonical id_type like "driver_license:NZ" (country optional) */
export const buildCanonicalIdType = (idType, countryCode) =>
  countryCode ? `${idType}:${countryCode}` : idType;

export const getIdLabel = (idType, countryCode, overrides = {}) => {
  const data = t(`${idType}.name`, { ns: 'id_document_types', returnObjects: true });
  let base;
  if (typeof data === 'string') {
    base = countryCode ? `${countryCode} ${data}` : data;
  } else if (data && typeof data === 'object') {
    base =
      (countryCode && data[countryCode]) ||
      data.default ||
      (countryCode ? `${countryCode} ${idType}` : idType);
  } else {
    base = countryCode ? `${countryCode} ${idType}` : idType;
  }
  // If a custom prefix is provided (e.g., "the back of"), strip leading "CC " to avoid "the back of AU Passport"
  let label = overrides?.prefix
    ? `${overrides.prefix} ${base.replace(/^([A-Z]{2})\s+/, '')}`
    : base;
  // If a custom suffix is provided (e.g., "(Issued on or after 1998)"), append it
  if (overrides?.suffix) {
    label = `${label} ${overrides.suffix}`;
  }
  return label;
};

/**
 * Requirement matching:
 * - If required includes a country (type:CC), require exact match.
 * - If required is base only (type), any variant with same base satisfies.
 */
export const matchesRequired = (uploadIdType, requiredKey) => {
  const up = parseIdKey(uploadIdType);
  const req = parseIdKey(requiredKey);
  if (!up.idType || !req.idType) return false;
  if (req.countryCode) return up.idType === req.idType && up.countryCode === req.countryCode;
  return up.idType === req.idType;
};

/**
 * Build a UI option for an ID doc.
 * allTypesMeta item shape (from /id-document-types):
 */
export const toIdOption = ({ idType, countryCode, meta = {}, overrides = {} } = {}) => {
  if (!idType) return null;

  const id_type = buildCanonicalIdType(idType, countryCode);
  const label = getIdLabel(idType, countryCode, overrides);

  let complementary_id = null;
  if (meta.complementaryCode) {
    const comp = meta.complementaryCode; // e.g., 'driver_license_back'
    const compCanonical = comp.includes(':') || !countryCode ? comp : `${comp}:${countryCode}`;
    complementary_id = {
      code: compCanonical,
      prefix: meta.complementaryPrefix || overrides.complementaryPrefix || 'the back of',
    };
  }

  return {
    idType,
    countryCode,
    id_type, // canonical; store this in upload metadata
    label, // user-facing label
    complementary_id,
    has_photo: !!meta.has_photo,
    provider: meta.provider ?? null,
  };
};

/**
 * Given specific codes (ordered) + allTypesMeta (array), return option list.
 * Codes can include region: ["passport:AU", "driver_license:NZ"].
 */
export const buildOptionsFromCodes = (codes = [], allTypesMeta = [], overridesByCode = {}) => {
  if (!Array.isArray(codes) || !codes.length) return [];
  const typesByCode = new Map(allTypesMeta.map(x => [x.code, x]));
  const out = [];

  for (const code of codes) {
    const { idType, countryCode } = parseIdKey(code);
    if (!idType) continue;
    const meta = typesByCode.get(idType) || {};
    const overrides = overridesByCode[code] || {};
    const opt = toIdOption({ idType, countryCode, meta, overrides });
    if (opt) out.push(opt);
  }
  return out;
};

/**
 * Build a generic, full list for support (optionally filtered).
 * filters: { photoIdOnly?: boolean, provider?: true|string }
 */
export const buildAllTypeOptions = (allTypesMeta = [], filters = {}) => {
  const { photoIdOnly = false, provider = null } = filters;
  let list = allTypesMeta;

  if (photoIdOnly) list = list.filter(x => !!x.has_photo);
  if (provider === true) {
    list = list.filter(x => !!x.provider);
  } else if (typeof provider === 'string') {
    list = list.filter(x => x.provider === provider);
  }

  return list.map(meta =>
    toIdOption({
      idType: meta.code,
      countryCode: null,
      meta,
    }),
  );
};
